import { Trans, useTranslation } from 'next-i18next/pages'
import { Card, Carousel, Container, Header, Heading, HeadingAndContent, Img, List, TextAndMedia } from '@boltenergy-be/design-system'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import styles from './BoltXPlenty.module.scss'
import { BoltXPlentyProps } from './types'
import { BOLT_X_PLENTY_FROM, TESTIMONIALS } from './constants'
import ReviewRatings from 'components/review-ratings/ReviewRatings'
import Link from 'components/link/Link'
import { PLENTY_INFO_NOTE } from 'constants/constants'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'
import SharesForm from 'features/bolt-x-plenty/components/shares-form/SharesForm'

const BoltXPlenty = ({ faqCategories }: BoltXPlentyProps) => {
  // i18n
  const { t } = useTranslation('boltXPlenty')

  // Router
  const { locale } = useRouter()

  return (
    <main>
      <Header title={t('header.title')} classnames={{ content: styles.header }} docked color="dark" shape="s5">
        <p>{parse(t('header.description'))}</p>
        <StartSimulationButton fromPage={BOLT_X_PLENTY_FROM} />
      </Header>

      <Container as="section" classnames={{ content: styles['shares-content'] }}>
        <Heading as="h2" variant="d3" className={styles.heading}>
          {t('shares.title')}
        </Heading>
        <p className={styles.desc}>{t('shares.description')}</p>

        <SharesForm />
      </Container>

      <TextAndMedia
        showMediaOnMobile
        inverse
        title={t('cycle.title')}
        media={{
          type: 'image',
          imageBehaviour: '1-1',
          props: { publicId: `website/bolt-x-plenty/cycle-${locale}`, fill: true, alt: '', objectFit: 'contain' }
        }}
      >
        <p>{t('cycle.p1')}</p>
        <p>{parse(t('cycle.p2'))}</p>
      </TextAndMedia>

      {/* ADVANTAGES */}
      <HeadingAndContent title={t('advantages.title')} color="accent-soft" inset>
        <List as="ul" bullets="badgeCheck">
          {(t('advantages.bullets', { returnObjects: true }) as string[]).map((bullet, index) => (
            <li key={`advantage-${index + 1}`}>
              <span>{parse(bullet)}</span>
            </li>
          ))}
        </List>

        <StartSimulationButton fromPage={BOLT_X_PLENTY_FROM} />

        <p className={styles.disclaimer}>
          <span>
            <Trans
              {...{ t }}
              i18nKey="advantages.disclaimer"
              components={[<Link href={PLENTY_INFO_NOTE[locale]} target="_blank" variant="inline" />]}
            />
          </span>
        </p>
      </HeadingAndContent>

      {/* TESTIMONIALS */}
      <Carousel
        title={t('testimonials.title')}
        aboveTitleSlot={<ReviewRatings show={{ googleReviews: true }} className="pt-500" />}
        color="light"
        autoscroll
        classnames={{ slide: styles['testimonial-slide'], heading: styles['testimonial-heading'] }}
      >
        {TESTIMONIALS.map(({ id, quote, author, location, avatar }) => (
          <Card key={`testimonial-${id}`} as="figure" color="warm" shadow={false} className={styles.testimonial}>
            <blockquote>“{quote[locale]}”</blockquote>
            <figcaption className={styles['testimonial-caption']}>
              {!!avatar && <Img publicId={avatar} width={48} height={48} className={styles.avatar} alt="" />}
              <strong className={styles.author}>{author}</strong>
              <small className={styles.location}>{location[locale]}</small>
            </figcaption>
          </Card>
        ))}
      </Carousel>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={faqCategories} title={t('faqs.title')} />
    </main>
  )
}

export default BoltXPlenty
